// src/components/Inventory/RemnantSheet.js
// Flujo "+ Retal" (§6.A + §6b): material existente (o categoría manual) →
// Medida 1 × Medida 2 en pulgadas + cantidad + ubicación de lista.
// REGLA §5b: si es cartón corrugado la UI rotula "Medida 1 — DIRECCIÓN DE
// FLAUTA"; el SERVIDOR fija fluteDirection='m1' (createRemnant).
// DXF al instante en cliente (geometría sin secretos); guardar = Cloud Function.
import React, { useMemo, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { functions, storage } from '../../firebase/firebase-config';
import { t } from '../../lib/i18n';
import { buildRectangleDxf, downloadDxf } from '../../lib/dxf';
import { remnantDxfFilename } from '../../lib/naming';
import { MATERIAL_CATEGORIES, isCorrugated, materialSpecLine } from '../../lib/materialCategories';
import { buildRemnantLabelPdf } from '../../lib/remnantLabel';
import { locationLabel } from '../../lib/locations';
import LocationPicker, { isLocationComplete } from './LocationPicker';

const RemnantSheet = ({ material, materials, cfg, onClose, onSaved }) => {
    // Material: fijado desde la tarjeta (flujo obrero) o elegible aquí (pestaña Retales).
    const [materialId, setMaterialId] = useState(material?.id || '');
    const [manualCategory, setManualCategory] = useState('Carton');
    const [m1, setM1] = useState('');
    const [m2, setM2] = useState('');
    const [quantity, setQuantity] = useState('1');
    const [location, setLocation] = useState(null);
    const [sourceJobId, setSourceJobId] = useState('');
    const [notes, setNotes] = useState('');
    const [photoFile, setPhotoFile] = useState(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [saved, setSaved] = useState(null); // { code, remnantId }

    const selMaterial = material || (materials || []).find((m) => m.id === materialId) || null;
    const category = selMaterial?.category || manualCategory;
    const corrugated = isCorrugated(category);

    const w = Number(m1); const h = Number(m2);
    const dimsOk = Number.isFinite(w) && w > 0 && Number.isFinite(h) && h > 0;
    const qty = Math.floor(Number(quantity));
    const canSave = dimsOk && qty >= 1 && isLocationComplete(location) && !busy && !saved;

    const payload = useMemo(() => ({
        materialId: selMaterial?.id || null,
        category: selMaterial ? undefined : manualCategory,
        m1: w, m2: h, quantity: qty,
        location, sourceJobId: sourceJobId.trim() || null, notes,
    }), [selMaterial, manualCategory, w, h, qty, location, sourceJobId, notes]);

    const handleDxf = () => {
        if (!dimsOk) { setError(t('remnant.invalidDims')); return; }
        setError('');
        const dxf = buildRectangleDxf({ widthIn: w, heightIn: h });
        downloadDxf(dxf, remnantDxfFilename({ code: saved?.code, widthIn: w, heightIn: h }));
    };

    const handleSave = async () => {
        setError('');
        setBusy(true);
        try {
            // Foto opcional de la pieza (§6.A paso 5) — sube a Storage antes de guardar.
            let imageUrl = null;
            if (photoFile) {
                const storageRef = ref(storage, `remnants/${Date.now()}_${photoFile.name}`);
                await uploadBytes(storageRef, photoFile);
                imageUrl = await getDownloadURL(storageRef);
            }
            const fn = httpsCallable(functions, 'createRemnant');
            const res = await fn({ ...payload, imageUrl });
            setSaved({ code: res.data.code, remnantId: res.data.remnantId });
            onSaved && onSaved();
        } catch (e) {
            console.error('createRemnant falló:', e);
            setError(e.message || t('remnant.error'));
        } finally {
            setBusy(false);
        }
    };

    const handleLabel = async () => {
        // Etiqueta §5c con la ubicación resuelta (paleta → zona actual).
        const remnant = {
            code: saved?.code, m1: w, m2: h, quantity: qty,
            category,
            fluteDirection: corrugated ? 'm1' : null,
            materialSnapshot: selMaterial ? {
                name: selMaterial.name, color: selMaterial.color,
            } : null,
            thickness: selMaterial?.thickness || null,
            location,
        };
        await buildRemnantLabelPdf({ remnant, locationText: locationLabel(location, cfg) });
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-end md:items-center z-50 overflow-y-auto">
            <div className="bg-white rounded-t-2xl md:rounded-xl shadow-2xl w-full md:max-w-lg p-5 space-y-4 max-h-[95vh] overflow-y-auto">
                <div className="flex items-center gap-3">
                    {selMaterial && (
                        <img src={selMaterial.imageUrl || 'https://placehold.co/100x100/eee/ccc?text=N/A'} alt={selMaterial.name}
                            className="w-14 h-14 object-cover rounded-md border" />
                    )}
                    <div>
                        <h2 className="text-lg font-bold text-gray-800">{t('remnant.title')}</h2>
                        <p className="text-sm text-gray-600">{selMaterial ? selMaterial.name : t('remnant.materialNone')}</p>
                        {selMaterial && materialSpecLine(selMaterial) && (
                            <p className="text-xs text-gray-500">📐 {materialSpecLine(selMaterial)}</p>
                        )}
                    </div>
                </div>

                {/* Selector de material (solo cuando no vino fijado desde la tarjeta) */}
                {!material && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        <div>
                            <label className="block text-sm font-medium text-gray-700">{t('remnant.material')}</label>
                            <select className="mt-1 w-full p-2 border border-gray-300 rounded-md" value={materialId}
                                onChange={(e) => setMaterialId(e.target.value)}>
                                <option value="">{t('remnant.materialNone')}</option>
                                {(materials || []).map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                            </select>
                        </div>
                        {!selMaterial && (
                            <div>
                                <label className="block text-sm font-medium text-gray-700">{t('remnant.category')}</label>
                                <select className="mt-1 w-full p-2 border border-gray-300 rounded-md" value={manualCategory}
                                    onChange={(e) => setManualCategory(e.target.value)}>
                                    {MATERIAL_CATEGORIES.map((c) => <option key={c}>{c}</option>)}
                                </select>
                            </div>
                        )}
                    </div>
                )}

                {/* Medidas — la REGLA de flauta rotulada en la UI (§5b) */}
                {corrugated && (
                    <p className="text-xs font-semibold text-amber-800 bg-amber-50 rounded-md p-2">⚠️ {t('remnant.fluteHint')}</p>
                )}
                {/* items-stretch + mt-auto: los inputs quedan ALINEADOS aunque el rótulo
                    de flauta (M1) ocupe 2 líneas y el de M2 solo 1. */}
                <div className="grid grid-cols-2 gap-3 items-stretch">
                    <div className="flex flex-col">
                        <label className={`block text-sm font-medium mb-1 ${corrugated ? 'text-amber-800' : 'text-gray-700'}`}>
                            {corrugated ? t('remnant.m1.flute') : t('remnant.m1')}
                        </label>
                        <input type="number" inputMode="decimal" min="0" step="0.01" value={m1}
                            onChange={(e) => setM1(e.target.value)} autoFocus disabled={!!saved}
                            className="mt-auto block w-full p-3 text-xl text-center border border-gray-300 rounded-md" />
                    </div>
                    <div className="flex flex-col">
                        <label className="block text-sm font-medium text-gray-700 mb-1">{t('remnant.m2')}</label>
                        <input type="number" inputMode="decimal" min="0" step="0.01" value={m2}
                            onChange={(e) => setM2(e.target.value)} disabled={!!saved}
                            className="mt-auto block w-full p-3 text-xl text-center border border-gray-300 rounded-md" />
                    </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <label className="block text-sm font-medium text-gray-700">{t('remnant.quantity')}</label>
                        <input type="number" inputMode="numeric" min="1" value={quantity}
                            onChange={(e) => setQuantity(e.target.value)} disabled={!!saved}
                            className="mt-1 block w-full p-2 text-center border border-gray-300 rounded-md" />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700">{t('remnant.sourceJob')}</label>
                        <input type="text" value={sourceJobId} onChange={(e) => setSourceJobId(e.target.value)}
                            placeholder="J-1042" disabled={!!saved}
                            className="mt-1 block w-full p-2 border border-gray-300 rounded-md" />
                    </div>
                </div>

                <LocationPicker cfg={cfg} value={location} onChange={setLocation} />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                        <label className="block text-sm font-medium text-gray-700">{t('remnant.notes')}</label>
                        <input type="text" value={notes} onChange={(e) => setNotes(e.target.value)} disabled={!!saved}
                            className="mt-1 block w-full p-2 border border-gray-300 rounded-md" />
                    </div>
                    <div>
                        {/* Foto opcional: en móvil abre la cámara directo (capture). */}
                        <label className="block text-sm font-medium text-gray-700">📷 {t('remnant.photo')}</label>
                        <input type="file" accept="image/*" capture="environment" disabled={!!saved}
                            onChange={(e) => setPhotoFile(e.target.files?.[0] || null)}
                            className="mt-1 block w-full text-sm text-gray-500 file:mr-2 file:py-2 file:px-3 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700" />
                    </div>
                </div>

                {error && <p className="text-sm text-red-600">{error}</p>}
                {saved && (
                    <p className="text-center font-bold text-green-700 text-lg">✓ {t('remnant.saved')} {saved.code}</p>
                )}

                <div className="flex flex-wrap gap-3">
                    <button onClick={handleDxf} disabled={!dimsOk}
                        className="flex-1 px-4 py-3 font-bold text-blue-700 bg-blue-50 border border-blue-300 rounded-lg hover:bg-blue-100 disabled:opacity-40">
                        ⬇ {t('remnant.downloadDxf')}
                    </button>
                    {!saved ? (
                        <button onClick={handleSave} disabled={!canSave}
                            className="flex-1 px-4 py-3 font-bold text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-40">
                            {busy ? t('remnant.saving') : t('remnant.save')}
                        </button>
                    ) : (
                        <button onClick={handleLabel}
                            className="flex-1 px-4 py-3 font-bold text-white bg-gray-800 rounded-lg hover:bg-black">
                            🏷 {t('remnant.printLabel')}
                        </button>
                    )}
                    <button onClick={onClose} className="w-full px-4 py-2 font-medium text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300">
                        {saved ? t('remnant.close') : t('remnant.cancel')}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default RemnantSheet;
